// IND-009: estado de cobertura de un material en el Panel, en cinco estados.
// Solo presentación: lee lo que ya calcula el motor (no cambia el cálculo,
// salvo la cifra de «Rotura hoy», que es el déficit al cierre de hoy).
import type { MaterialStockProjectionRow } from '../types/industrialCost';

import { esNumberFormat } from './numberFormat';
import { validUnit } from './plantPanel';
export type CoverageStateKey = 'rotura' | 'confirmar' | 'tarde' | 'sin_stock' | 'cubierto';

export type CoverageState = {
  key: CoverageStateKey;
  label: string;
  detail: string;
};

export const COVERAGE_STATE_LABELS: Record<CoverageStateKey, string> = {
  rotura: 'Rotura',
  confirmar: 'Confirmar compra',
  tarde: 'Llega tarde',
  sin_stock: 'Sin stock calculado',
  cubierto: 'Cubierto',
};

export function shortDay(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}` : '—';
}

export function formatQty(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({ maximumFractionDigits: 0 }).format(value).replace('-', '−');
}

/** Saldo al cierre de hoy: tras las compras que llegan hoy y las necesidades
 * de hoy y las atrasadas (todas tienen fecha efectiva hoy). */
export function closingBalanceToday(row: MaterialStockProjectionRow, today: string | null | undefined): number | null {
  if (row.calculated_stock_qty == null) return null;
  const day = (today ?? '').slice(0, 10);
  let balance: number = row.calculated_stock_qty;
  for (const event of row.timeline_events ?? []) {
    if (day && event.effective_date.slice(0, 10) <= day) {
      balance = event.balance_after_qty;
    }
  }
  return balance;
}

/** IND-015: una sola palabra bajo la compra: «vencida», «sin fecha» o «llega dd/mm». */
export function purchaseNote(row: MaterialStockProjectionRow): { text: string; warn: boolean } {
  if ((row.incoming_overdue_qty ?? 0) > 0) return { text: 'vencida', warn: true };
  if ((row.incoming_undated_qty ?? 0) > 0) return { text: 'sin fecha', warn: true };
  if (row.next_delivery_date) return { text: `llega ${shortDay(row.next_delivery_date)}`, warn: false };
  return { text: '', warn: false };
}

export function coverageState(row: MaterialStockProjectionRow, today: string | null | undefined): CoverageState {
  // IND-015: sin «nan» como unidad.
  const validLabel = validUnit(row.unit_label);
  const unit = validLabel ? ` ${validLabel}` : '';

  if (row.coverage_status === 'missing_stock' || row.calculated_stock_qty == null) {
    return {
      key: 'sin_stock',
      label: COVERAGE_STATE_LABELS.sin_stock,
      detail: row.status === 'partial' ? 'producción sin fecha diaria' : 'sin recuento',
    };
  }

  if (row.coverage_status === 'late_delivery') {
    const nextAfter = (row.purchase_events ?? [])
      .filter((event) => event.purchase_status === 'dated' && event.effective_date)
      .map((event) => event.effective_date as string)
      .filter((day) => !row.first_shortage_date || day > row.first_shortage_date)
      .sort()[0];
    return {
      key: 'tarde',
      label: COVERAGE_STATE_LABELS.tarde,
      detail: `rotura ${shortDay(row.first_shortage_date)}, compra ${shortDay(nextAfter)}`,
    };
  }

  if (row.first_shortage_date) {
    const day = (today ?? '').slice(0, 10);
    const isToday = Boolean(day) && row.first_shortage_date.slice(0, 10) <= day;
    const why = row.coverage_status === 'no_purchase' ? 'sin compra' : 'compra insuficiente';
    if (isToday) {
      const closing = closingBalanceToday(row, today);
      const deficit = closing != null && closing < 0 ? -closing : row.first_shortage_qty;
      return {
        key: 'rotura',
        label: COVERAGE_STATE_LABELS.rotura,
        detail: `Rotura hoy · faltan ${formatQty(deficit)}${unit} · ${why}`,
      };
    }
    return {
      key: 'rotura',
      label: COVERAGE_STATE_LABELS.rotura,
      detail: `${shortDay(row.first_shortage_date)} · faltan ${formatQty(row.first_shortage_qty)}${unit} · ${why}`,
    };
  }

  // Cubierto gracias a una compra vencida o sin fecha: sin ella habría rotura
  // algún día (se recorre la línea por fechas sin esas compras).
  if (wouldBreakWithoutUnconfirmed(row)) {
    return { key: 'confirmar', label: COVERAGE_STATE_LABELS.confirmar, detail: 'cubre solo si llega' };
  }

  return { key: 'cubierto', label: COVERAGE_STATE_LABELS.cubierto, detail: '' };
}

export function wouldBreakWithoutUnconfirmed(row: MaterialStockProjectionRow): boolean {
  const unconfirmed = (row.incoming_overdue_qty ?? 0) + (row.incoming_undated_qty ?? 0);
  if (unconfirmed <= 0 || row.calculated_stock_qty == null) return false;
  let balance = row.calculated_stock_qty;
  for (const event of row.timeline_events ?? []) {
    if (
      event.event_type === 'purchase' &&
      (event.purchase_status === 'overdue' || event.purchase_status === 'undated')
    ) {
      continue;
    }
    balance += event.quantity_delta;
    if (balance < 0) return true;
  }
  return balance < 0;
}

export type CoverageSortKey = 'stock' | 'need' | 'purchase' | 'balance';

export function coverageSortValue(row: MaterialStockProjectionRow, key: CoverageSortKey): number | null {
  if (key === 'stock') return row.calculated_stock_qty;
  if (key === 'need') return row.pending_requirement_qty;
  if (key === 'purchase') return row.incoming_pending_qty;
  return row.eventual_balance_qty;
}

/** Orden por columna; los valores vacíos («—») siempre al final. */
export function sortCoverageRows(
  rows: MaterialStockProjectionRow[],
  key: CoverageSortKey,
  direction: 'asc' | 'desc',
): MaterialStockProjectionRow[] {
  return [...rows].sort((a, b) => {
    const va = coverageSortValue(a, key);
    const vb = coverageSortValue(b, key);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return direction === 'asc' ? va - vb : vb - va;
  });
}
